/**
 * RoleGate — the client mirror of backend `@require(role, scope)`.
 *
 * Two distinct behaviours the design calls out (sec 7):
 *   - HIDE: a role that should never SEE a control (read_only never sees a
 *     mutating action) is given nothing to render.
 *   - DISABLE: a control that exists for this operator's job but they lack the
 *     privilege for *right now* renders disabled + a tooltip explaining why —
 *     "insufficient-but-present = disabled+tooltip". This teaches the model
 *     instead of hiding capability.
 *
 * This is a UX affordance ONLY. The server re-checks every mutation against
 * g.auth — RoleGate never enforces security, it just avoids dead-end clicks.
 */
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import type { Role } from "@/types/domain";
import { useSession } from "@/stores/session";

interface RoleGateProps {
  /** Minimum role required at the current scope. */
  min: Role;
  children: ReactNode;
  /**
   * "hide" (default) removes the control entirely — use for anything a lower
   * role should never know exists. "disable" keeps it visible but inert with a
   * tooltip — use for actions within the operator's remit but above their grant.
   */
  mode?: "hide" | "disable";
  /** Tooltip copy for disable mode. */
  reason?: string;
  /** Optional fallback for hide mode (e.g. a read-only rendering). */
  fallback?: ReactNode;
}

export function RoleGate({
  min,
  children,
  mode = "hide",
  reason = "You do not have permission for this action at the current scope.",
  fallback = null,
}: RoleGateProps) {
  const allowed = useSession((s) => s.hasRole(min));

  if (allowed) return <>{children}</>;

  if (mode === "hide") return <>{fallback}</>;

  // disable mode: clone the single child, force it disabled + aria, wrap in a
  // tooltip that surfaces the reason on hover/focus (keyboard-reachable).
  const disabledChild = isValidElement(children)
    ? cloneElement(children as ReactElement, {
        disabled: true,
        "aria-disabled": true,
        tabIndex: 0, // keep focusable so the tooltip is keyboard-reachable
      })
    : children;

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span className="inline-flex">{disabledChild}</span>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          sideOffset={6}
          className="z-toast max-w-xs rounded bg-ink px-3 py-2 text-xs text-ink-invert shadow-sheet"
        >
          {reason}
          <Tooltip.Arrow className="fill-ink" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
